import { useInView } from "framer-motion"
import * as React from "react"
import styled from "styled-components"
import { Spacer } from "../atoms/spacers"
import NotificationSymbol from "../atoms/NotificationSymbol"
import H3 from "../atoms/typography/H3"
import PrimaryButton from "../atoms/buttons/PrimaryButton"
import { AddIcon } from "../atoms/icons/AddIcon"
import { ClearAllIcon } from "../atoms/icons/ClearAllIcon"
import AddDataSection from "./AddDataSection"
import { useUpdateReadSharedPlanNotification } from "../../hooks/useUpdateReadSharedPlanNotification"
import { useUpdateReadSinglePlanNotification } from "../../hooks/useUpdateReadsinglePlanNotification"
import { useClearUserNotifications } from "../../hooks/useClearUserNotification"
import { Col, Hidden, Visible } from "react-grid-system"
import { MediaQuery } from "../../utils/design-helpers"
import { MoreVerticalIcon } from "../atoms/icons/MoreVerticalIcon"
import { captureExceptionWithContext } from "../../utils/sentry"
import { ApolloError } from "@apollo/client"

interface Props {
  read: boolean
  message: string
  id: number | string
  isSinglePlanNotification: boolean
  apiPlanId: number | string
}

const Bar = styled.div<{ open?: boolean }>`
  padding: ${(props) => props.theme.spacings.large};
  border-radius: ${(props) => props.theme.borders.softRadius};
  background-color: ${(props) => (props.open ? props.theme.colors.greyLight : undefined)};

  :hover {
    background-color: ${(props) => props.theme.colors.greyLight};
  }

  ${MediaQuery.XS} {
    padding: ${(props) => props.theme.spacings.small};
  }
`

const RowContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const UserNotificationBar: React.FunctionComponent<Props> = ({
  read,
  message,
  id,
  isSinglePlanNotification,
  apiPlanId,
}) => {
  const ref = React.useRef(null)
  const isInView = useInView(ref, { once: true })
  const [addDataOpen, setAddDataOpen] = React.useState(false)

  const onCompletedReadShared = () => {
    console.log("Updated read status och shared notification")
  }
  const onErrorReadShared = (error: ApolloError) => {
    captureExceptionWithContext(error, "error updating read status on shared notification")
  }
  const { updateReadSharedPlanNotification } = useUpdateReadSharedPlanNotification({
    onCompleted: onCompletedReadShared,
    onError: onErrorReadShared,
  })
  const onUpdateReadShareDnotification = async () => {
    try {
      await updateReadSharedPlanNotification({
        variables: {
          id: id,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error updating read status on shared notification")
    }
  }
  const onCompletedReadSingle = () => {
    console.log("Updated read status och single notification")
  }
  const onErrorReadSingle = (error: ApolloError) => {
    captureExceptionWithContext(error, "error updating read status on single notification")
  }
  const { updateReadSinglePlanNotification } = useUpdateReadSinglePlanNotification({
    onCompleted: onCompletedReadSingle,
    onError: onErrorReadSingle,
  })
  const onUpdateReadSingleNotification = async () => {
    try {
      await updateReadSinglePlanNotification({
        variables: {
          id: id,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error updating read status on single notification")
    }
  }

  const onClickMarkAsReadOrUnread = () => {
    if (isSinglePlanNotification) {
      onUpdateReadSingleNotification()
    } else {
      onUpdateReadShareDnotification()
    }
  }

  React.useEffect(() => {
    if (!read) {
      if (isInView) {
        onClickMarkAsReadOrUnread()
      }
    }
  }, [isInView])

  const onCompletedClearNotification = () => {
    console.log("Cleared user notification")
  }
  const onErrorClearlNotification = (error: ApolloError) => {
    captureExceptionWithContext(error, "error clearing user notification")
  }
  const { clearUserNotification } = useClearUserNotifications({
    onCompleted: onCompletedClearNotification,
    onError: onErrorClearlNotification,
  })

  const onClearUserNotification = async () => {
    try {
      await clearUserNotification({
        variables: {
          id: id,
          isSinglePlan: isSinglePlanNotification,
        },
      })
    } catch (error) {
      captureExceptionWithContext(error, "error clearing user notification")
    }
  }

  return (
    <Bar ref={ref} open={addDataOpen}>
      <RowContent>
        <div style={{ display: "flex", alignItems: "center" }}>
          <Visible xs>
            <Col xs={1} md={1}>
              <NotificationSymbol read={read} />
            </Col>
          </Visible>
          <Hidden xs>
            <NotificationSymbol read={read} />
          </Hidden>

          <Spacer spacing={0.5} />
          <H3>{message}</H3>
        </div>
        <Visible xs>
          <Col xs={1}>
            <MoreVerticalIcon />
          </Col>
        </Visible>
        <Hidden xs>
          <div style={{ display: "flex" }}>
            <PrimaryButton title="Clear" icon={<ClearAllIcon />} onClick={onClearUserNotification} />
            <Spacer spacing={0.5} />
            {read ? (
              <PrimaryButton
                title="Mark as unread"
                icon={<NotificationSymbol read={false} />}
                onClick={onClickMarkAsReadOrUnread}
              />
            ) : (
              <PrimaryButton
                title="Mark as read"
                icon={<NotificationSymbol read={true} />}
                onClick={onClickMarkAsReadOrUnread}
              />
            )}
            <Spacer spacing={0.5} />
            <PrimaryButton title="Add data" icon={<AddIcon />} onClick={() => setAddDataOpen(!addDataOpen)} />
          </div>
        </Hidden>
      </RowContent>
      <AddDataSection isOpen={addDataOpen} onClickCancel={() => setAddDataOpen(false)} apiPlanId={apiPlanId} />
    </Bar>
  )
}

export default UserNotificationBar
